/**
 * Auth Context
 *
 * Persists the user session through `services/api.js`'s storeSession /
 * clearSession helpers so the access + refresh + user trio always stay
 * in sync with the API client's localStorage keys.
 */

import React, { createContext, useContext, useState, useEffect } from 'react';
import {
  authAPI,
  storeSession,
  clearSession,
  getAccessToken,
} from '../services/api';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [loading, setLoading] = useState(true);

  // Initialise from localStorage once on mount.
  useEffect(() => {
    const storedToken = getAccessToken('user');
    const storedUser  = localStorage.getItem('user');
    if (storedToken && storedUser) {
      setToken(storedToken);
      setUser(JSON.parse(storedUser));
    }
    setLoading(false);
  }, []);

  // Both login and register now return { access_token, refresh_token, user }
  // alongside the legacy `token` field. Use the new helpers so all three
  // keys land consistently in localStorage.
  const persist = (payload) => {
    const access_token  = payload.access_token  || payload.token;
    const refresh_token = payload.refresh_token;
    const u             = payload.user;
    storeSession('user', { access_token, refresh_token, user: u });
    setUser(u);
    setToken(access_token);
  };

  // Exposed so VerifyEmail.js can persist a session after token verification.
  const persistSession = (payload) => persist(payload);

  const login = async (credentials) => {
    try {
      const response = await authAPI.login(credentials);
      persist(response.data);
      return { success: true };
    } catch (error) {
      if (error.response?.status === 429) {
        return {
          success: false,
          rateLimited: true,
          retryAfter: error.response.data?.retry_after ?? 60,
          error: error.message,
        };
      }
      const code = error.response?.data?.code;
      if (code === 'email_not_verified') {
        return { success: false, emailNotVerified: true, error: error.message };
      }
      return { success: false, error: error.message };
    }
  };

  const register = async (userData) => {
    try {
      const response = await authAPI.register(userData);
      // Backend may return verification_pending instead of tokens.
      const payload = response.data ?? response;
      // Backend wraps data in { data: { status } } envelope.
      const inner = payload?.data ?? payload;
      if (inner?.status === 'verification_pending') {
        return { success: true, verificationPending: true };
      }
      persist(payload);
      return { success: true };
    } catch (error) {
      return { success: false, error: error.message };
    }
  };

  const resendVerification = async (email) => {
    try {
      await authAPI.resendVerification(email);
      return { success: true };
    } catch (error) {
      return { success: false, error: error.message };
    }
  };

  const logout = async () => {
    // authAPI.logout calls /auth/logout (revoking the refresh token) before
    // wiping local storage. Safe to await — it swallows network errors.
    await authAPI.logout();
    clearSession('user');
    setUser(null);
    setToken(null);
  };

  const isAuthenticated = () => !!token && !!user;

  const updateUser = (updatedData) => {
    setUser((prev) => {
      const next = { ...prev, ...updatedData };
      localStorage.setItem('user', JSON.stringify(next));
      return next;
    });
  };

  // Refresh from /users/me — the api client interceptor will handle any
  // 401 by silently refreshing the access token first.
  const refreshUser = async () => {
    if (!token) return;
    try {
      const response = await fetch('/api/users/me', {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${getAccessToken('user')}`,
          'Content-Type': 'application/json',
        },
      });
      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          setUser(data.data);
          localStorage.setItem('user', JSON.stringify(data.data));
        }
      } else if (response.status === 401) {
        // Bare fetch can't refresh — fall through to logout so the UI
        // bounces to /login. The api client handles refresh on its own paths.
        await logout();
      }
    } catch (error) {
      console.error('Error refreshing user data:', error);
    }
  };

  const value = {
    user,
    token,
    loading,
    login,
    register,
    logout,
    isAuthenticated,
    updateUser,
    refreshUser,
    persistSession,
    resendVerification,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
};

export default AuthContext;
